import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { CONTACT } from "@/config/contact";
import VideoEmbed from "@/components/VideoEmbed";
import TrustBadge from "@/components/TrustBadge";
import { CheckCircle2, Quote, Star } from "lucide-react";

export default function SobrePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const values = [
    { title: "Honestidade primeiro", desc: "Só digo o que faz sentido. Uma verdade desconfortável hoje evita um erro caro amanhã." },
    { title: "Serviço próximo", desc: "Trabalho um número limitado de clientes por mês. Cada processo tem toda a minha atenção." },
    { title: "Rigor com humanidade", desc: "Documentos, prazos e negociação tratados com competência, mas sempre com o lado humano em cima da mesa." },
  ];

  return (
    <div data-testid="page-sobre">
      <section className="container-cnp pt-14 lg:pt-20 pb-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <span className="tag-chip">Sobre</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05]">
            Zélia Silva.<br/><span className="italic text-[#103C4A]">10+ anos no imobiliário.</span>
          </h1>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed">
            Sou uma profissional do setor imobiliário com mais de 10 anos de experiência dedicados a
            maximizar o valor e a experiência para os meus clientes — sejam eles compradores ou vendedores.
          </p>
          <p className="mt-4 text-stone-600 leading-relaxed">
            Trabalho principalmente no Grande Porto, com foco em Rio Tinto e Gondomar, Porto, Maia,
            Ermesinde e Valongo. Como consultora associada à <strong>eXp Portugal</strong>, tenho ao meu
            lado uma rede internacional que me permite oferecer um serviço completo.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contacto" data-testid="sobre-cta-contacto" className="btn-primary">Marcar reunião</Link>
            <Link to="/avaliacao-gratuita" data-testid="sobre-cta-avaliacao" className="btn-secondary">Pedir avaliação</Link>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-[#103C4A]/5" aria-hidden />
            <img src={CONTACT.photo} alt="Zélia Silva" className="relative rounded-3xl object-cover h-[520px] w-full" />
            <div className="absolute -bottom-5 left-6">
              <TrustBadge />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-stone-200/70">
        <div className="container-cnp py-16 lg:py-20">
          <span className="tag-chip">Método</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Três coisas em que não faço concessões.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <div key={i} data-testid={`sobre-value-${i}`} className="card-soft p-8">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#103C4A]/10 text-[#103C4A]"><CheckCircle2 className="h-5 w-5" /></div>
                <h3 className="mt-4 text-lg text-stone-900">{v.title}</h3>
                <p className="mt-2 text-sm text-stone-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {settings.sobre_video_url && (
        <section className="container-cnp py-16 lg:py-20">
          <div className="max-w-2xl">
            <span className="tag-chip">Uma palavra</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Um minuto comigo, em vídeo.</h2>
            <p className="mt-4 text-stone-600">Se preferir ouvir em vez de ler, deixo aqui uma apresentação curta.</p>
          </div>
          <div className="mt-8 max-w-4xl">
            <VideoEmbed url={settings.sobre_video_url} title="Zélia Silva" />
          </div>
        </section>
      )}

      {testimonials.some((t) => t.video_url) && (
        <section className="bg-white border-y border-stone-200/70">
          <div className="container-cnp py-16 lg:py-20">
            <span className="tag-chip">Depoimentos em vídeo</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">A voz de quem já cá passou.</h2>
            <div className="mt-10 grid md:grid-cols-2 gap-6">
              {testimonials.filter((t) => t.video_url).slice(0, 4).map((t) => (
                <div key={t.id} data-testid={`sobre-video-testimonial-${t.id}`} className="card-soft overflow-hidden">
                  <VideoEmbed url={t.video_url} title={t.name} />
                  <div className="p-6">
                    <div className="text-sm font-medium text-stone-900">{t.name}</div>
                    <div className="text-xs text-stone-500 capitalize">{t.client_type} · {t.zone}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="container-cnp py-16 lg:py-20">
        <div className="max-w-2xl">
          <span className="tag-chip">Clientes</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Histórias reais de quem já cá passou.</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`sobre-testimonial-${t.id}`} className="card-soft p-8">
              <Quote className="h-6 w-6 text-[#103C4A]/40" />
              <p className="mt-4 text-stone-700 leading-relaxed">&ldquo;{t.testimonial}&rdquo;</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#103C4A]/10 text-[#103C4A] flex items-center justify-center font-medium">{t.name.charAt(0)}</div>
                <div>
                  <div className="text-sm font-medium text-stone-900">{t.name}</div>
                  <div className="text-xs text-stone-500 capitalize">{t.client_type} · {t.zone}</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {[...Array(t.rating || 5)].map((_, i) => (<Star key={i} className="h-3.5 w-3.5 fill-[#103C4A] text-[#103C4A]" />))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
